import { useCallback, useEffect, useState } from 'react';
import axios from '@/lib/axios.js';
import { useToast } from '@/hooks/use-toast.js';
import CreateSecretShopperButton from './CreateSecretShopperButton.jsx';
import SecretShopperReportRow from './SecretShopperReportRow.jsx';
import SecretShopperReportSkeleton from './Skeleton.jsx';
import { Dialog, DialogContent, DialogFooter, DialogHeader } from '@/components/ui/dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import InfoCard from '../CommunityPages/SecretShopper/InfoCard.jsx';
import { Mail, Phone, User, Users } from 'lucide-react';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table.jsx';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination.jsx';
import { useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';

const SecretShoppersPage = () => {
    const [secretShoppers, setSecretShoppers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSecretShopper, setSelectedSecretShopper] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isRecordStepsModalOpen, setIsRecordStepsModalOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        limit: 10,
        total_pages: 0
    });
    const { toast } = useToast();

    const debouncedFetch = useCallback(
        debounce(async (params) => {
            try {
                setLoading(true);
                const { data } = await axios.get(`/secret-shopper?${params}`);
                setSecretShoppers(data.data);
                setPagination({
                    total: data.pagination.total,
                    page: data.pagination.page,
                    limit: data.pagination.limit,
                    total_pages: data.pagination.total_pages
                });
            } catch (error) {
                console.error('Error fetching communities:', error);
                toast({
                    title: "Error",
                    description: "Failed to load communities. Please try again.",
                    variant: "destructive",
                });
            } finally {
                setLoading(false);
            }
        }, 300),
        []
    );

    useEffect(() => {
        const params = {
            page: searchParams.get('page') || 1,
            limit: pagination.limit,
        };

        const encodedParams = new URLSearchParams(params).toString();
        debouncedFetch(encodedParams);
    }, [searchParams]);

    const handleCreatedSecretShopper = (newSecretShopper) => {
        setSecretShoppers([newSecretShopper, ...secretShoppers]);
        setSelectedSecretShopper(newSecretShopper);
        setIsRecordStepsModalOpen(true)
    }

    const handleLeadSubmitted = async () => {
        setIsSubmitting(true);

        if (!selectedSecretShopper?.id) {
            return;
        }

        try {
            await axios.post(`/secret-shopper/${selectedSecretShopper?.id}/initiate`);
            setIsRecordStepsModalOpen(false);
            setIsSubmitting(false)
            setSecretShoppers(prev =>
                prev.map(shopper =>
                    shopper.id === selectedSecretShopper.id 
                    ? { ...shopper, has_initiated_log: true }
                    : shopper
                )
            );
            setSelectedSecretShopper({})
        } catch (error) {
            console.error(error);
            toast({ title: "Error Initiating", variant: "destructive" });
            setIsSubmitting(false);
        }
    }

    const handleInitiateCallback = (secretShopper) => {
        setSelectedSecretShopper(secretShopper);
        setIsRecordStepsModalOpen(true)
    }


    const handlePageChange = (newPage) => {
        if (newPage !== 1) {
            searchParams.set('page', newPage);
        } else {
            searchParams.delete('page');
        }
        setPagination(prev => ({ ...prev, page: newPage }));
        setSearchParams(searchParams);
    };

    if (loading) {
        return <SecretShopperReportSkeleton />;
    }

    return (
        <div className="w-full max-w-7xl mx-auto mt-6">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-3xl font-semibold text-slate-900">Secret Shops</h1>
                <CreateSecretShopperButton handleCreatedSecretShopper={handleCreatedSecretShopper} />
            </div>

            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow className="h-20">
                            <TableHead className="w-10"></TableHead>
                            <TableHead className="w-10">Community</TableHead>
                            <TableHead className="w-10 text-nowrap">Created Date</TableHead>
                            <TableHead className="text-center w-20">Call</TableHead>
                            <TableHead className="text-center w-20">Text</TableHead>
                            <TableHead className="text-center w-20">Email</TableHead>
                            <TableHead className="text-center w-20">Voicemail</TableHead>
                            <TableHead className="text-center w-20">Status</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {secretShoppers.map((shop) => (
                            <SecretShopperReportRow key={shop.id} secretShopper={shop} initiateCallback={handleInitiateCallback} />
                        ))}
                    </TableBody>
                </Table>
            </div>

            {!loading && pagination.total_pages > 1 && (
                <div className="flex justify-center mt-6">
                    <Pagination currentPage={pagination.page} lastPage={pagination.total_pages}>
                        <PaginationContent>
                            <PaginationItem>
                                <PaginationPrevious
                                    onClick={() => handlePageChange(pagination.page - 1)}
                                />
                            </PaginationItem>

                            {[...Array(pagination.total_pages)].map((_, index) => {
                                const pageNumber = index + 1;

                                // Show first page, last page, and 1 page around current page
                                if (
                                    pageNumber === 1 ||
                                    pageNumber === pagination.total_pages ||
                                    (pageNumber >= pagination.page - 1 && pageNumber <= pagination.page + 1)
                                ) {
                                    return (
                                        <PaginationItem key={pageNumber}>
                                            <PaginationLink
                                                onClick={() => handlePageChange(pageNumber)}
                                                isActive={pageNumber === pagination.page}
                                            >
                                                {pageNumber}
                                            </PaginationLink>
                                        </PaginationItem>
                                    );
                                }

                                // Show ellipsis for gaps
                                if (
                                    pageNumber === 2 ||
                                    pageNumber === pagination.total_pages - 1
                                ) {
                                    return (
                                        <PaginationItem key={pageNumber}>
                                            <PaginationEllipsis />
                                        </PaginationItem>
                                    );
                                }

                                return null;
                            })}

                            <PaginationItem>
                                <PaginationNext onClick={() => handlePageChange(pagination.page + 1)} />
                            </PaginationItem>
                        </PaginationContent>
                    </Pagination>
                </div>
            )}

            <Dialog open={isRecordStepsModalOpen} onOpenChange={setIsRecordStepsModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <p className="text-gray-700">
                            <i className="underline font-semibold">Follow these steps:</i>
                            <ol className="list-decimal list-inside mt-4 space-y-2 grid gap-2 mb-4">
                                <li>
                                    Copy the prospect details using the copy buttons.
                                </li>
                                <li>
                                    Submit / paste them via the ILS or website contact form.
                                    {!!selectedSecretShopper?.websiteUrl && (
                                        <>
                                            <br />
                                            <a
                                                className="underline hover:text-blue-500"
                                                href={selectedSecretShopper.websiteUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Here is a link to your website
                                            </a>
                                        </>
                                    )}
                                </li>
                                <li>
                                    When done, click "I submitted the lead".
                                </li>
                            </ol>

                            <div className="grid grid-cols-1 gap-4 mb-4">
                                <InfoCard
                                    hasCopyToClipboard
                                    icon={User}
                                    label="First Name"
                                    value={selectedSecretShopper?.first_name}
                                />
                                <InfoCard
                                    hasCopyToClipboard
                                    icon={Users}
                                    label="Last Name"
                                    value={selectedSecretShopper?.last_name}
                                />
                                <InfoCard
                                    hasCopyToClipboard
                                    icon={Mail}
                                    label="Email"
                                    value={selectedSecretShopper?.email}
                                />
                                <InfoCard
                                    hasCopyToClipboard
                                    icon={Phone}
                                    label="Phone Number"
                                    value={selectedSecretShopper?.phone_number?.replace(/^(\+1)/, '').replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}
                                />
                            </div>
                        </p>
                    </DialogHeader>
                    <DialogFooter>
                        <Button onClick={handleLeadSubmitted} disabled={isSubmitting}>
                            I submitted the lead
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SecretShoppersPage;
